import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  FormControlLabel,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  RO_ACCOUNTING_OPTIONS,
  getAccountingLabel,
} from "./accountingPlanRo";

function computeIsoWeek(dateInput) {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "-";
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil((((utc - yearStart) / 86400000) + 1) / 7);
}

const initialState = {
  vendor_id: "",
  tip_plata: "ONE_TIME",
  serviciu: "",
  cont_contabil: [],
  denumire: "",
  numar: "",
  data_emitere: new Date().toISOString().slice(0, 10),
  termen_scadent: new Date().toISOString().slice(0, 10),
  valuta: "RON",
  suma_valuta: "",
  transmisa_in_spv: false,
  info: "",
};


function InvoiceFormModal({
  open,
  onClose,
  onSubmit,
  onUpload,
  onPreviewFx,
  onAiAutoImport,
  vendors = [],
}) {
  const [form, setForm] = useState(initialState);
  const [mode, setMode] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState({ sumaRon: 0, cursBnr: null, cursDate: null });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [aiFiles, setAiFiles] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState({ current: 0, total: 0 });
  const [aiError, setAiError] = useState("");
  const [aiResult, setAiResult] = useState("");

  const canPreview = useMemo(
    () => Number(form.suma_valuta) > 0 && Boolean(form.valuta) && Boolean(form.data_emitere),
    [form.suma_valuta, form.valuta, form.data_emitere]
  );
  const lunaEmiterii = useMemo(
    () => (form.data_emitere ? String(form.data_emitere).slice(0, 7) : "-"),
    [form.data_emitere]
  );
  const saptamanaScadentei = useMemo(
    () => computeIsoWeek(form.termen_scadent),
    [form.termen_scadent]
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!canPreview) {
        setPreview({ sumaRon: 0, cursBnr: null, cursDate: null });
        return;
      }
      if (String(form.valuta || "").toUpperCase() === "RON") {
        setPreview({
          sumaRon: Number(form.suma_valuta || 0),
          cursBnr: null,
          cursDate: null,
        });
        return;
      }
      try {
        setPreviewLoading(true);
        const data = await onPreviewFx({
          valuta: form.valuta,
          suma_valuta: Number(form.suma_valuta || 0),
          data_emitere: form.data_emitere,
        });
        if (!cancelled) setPreview(data);
      } catch (_) {
        if (!cancelled) setPreview({ sumaRon: 0, cursBnr: null, cursDate: null });
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [form.valuta, form.suma_valuta, form.data_emitere, canPreview, onPreviewFx]);

  const resetAndClose = () => {
    setForm(initialState);
    setMode(0);
    setFile(null);
    setFormError("");
    setAiFiles([]);
    setAiLoading(false);
    setAiProgress({ current: 0, total: 0 });
    setAiError("");
    setAiResult("");
    setPreview({ sumaRon: 0, cursBnr: null, cursDate: null });
    onClose();
  };

  const handleSave = async () => {
    if (!form.vendor_id) {
      setFormError("Selecteaza furnizorul inainte de salvare.");
      return;
    }

    let docUrl = null;
    if (file) {
      const uploaded = await onUpload(file);
      docUrl = uploaded.url;
    }

    await onSubmit({
      ...form,
      vendor_id: Number(form.vendor_id),
      cont_contabil: Array.isArray(form.cont_contabil) ? form.cont_contabil.join(", ") : form.cont_contabil,
      suma_valuta: Number(form.suma_valuta || 0),
      transmisa_in_spv: form.transmisa_in_spv ? 1 : 0,
      doc_plata_url: docUrl,
    });

    resetAndClose();
  };

  return (
    <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="md">
      <DialogTitle>Adauga factura</DialogTitle>
      <DialogContent>
        <Tabs value={mode} onChange={(_, value) => setMode(value)} sx={{ mb: 1 }}>
          <Tab label="Manual" />
          <Tab label="AI Auto" />
        </Tabs>
        {mode === 0 && (
          <>
        <Typography className="sf-modal-subtitle">Date baza</Typography>
        <Box className="sf-modal-grid">
          <TextField
            select
            label="Furnizor"
            value={form.vendor_id}
            onChange={(e) => {
              const nextVendorId = e.target.value;
              const selected = (vendors || []).find((v) => String(v.id) === String(nextVendorId));
              const preferredTip = String(selected?.preferred_tip_plata || "").toUpperCase();
              setForm((s) => ({
                ...s,
                vendor_id: nextVendorId,
                denumire: selected?.denumire || "",
                tip_plata: preferredTip === "RECURRENT" || preferredTip === "ONE_TIME"
                  ? preferredTip
                  : s.tip_plata,
              }));
              setFormError("");
            }}
            required
          >
            <MenuItem value="">Selecteaza furnizor</MenuItem>
            {(vendors || []).map((vendor) => (
              <MenuItem key={vendor.id} value={vendor.id}>
                {vendor.denumire}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Tip"
            value={form.tip_plata}
            onChange={(e) => setForm((s) => ({ ...s, tip_plata: e.target.value }))}
          >
            <MenuItem value="ONE_TIME">ONE_TIME</MenuItem>
            <MenuItem value="RECURRENT">RECURRENT</MenuItem>
          </TextField>
          <TextField
            label="Serviciu"
            value={form.serviciu}
            onChange={(e) => setForm((s) => ({ ...s, serviciu: e.target.value }))}
          />
          <Autocomplete
            multiple
            freeSolo
            options={RO_ACCOUNTING_OPTIONS}
            getOptionLabel={(option) => {
              if (typeof option === "string") return getAccountingLabel(option.split(" - ")[0] || option);
              return option?.label || "";
            }}
            value={Array.isArray(form.cont_contabil) ? form.cont_contabil : []}
            onChange={(_, value) =>
              setForm((s) => ({
                ...s,
                cont_contabil: (value || []).map((v) => {
                  if (typeof v === "string") return String(v).split(" - ")[0].trim();
                  return String(v?.code || "").trim();
                }).filter(Boolean),
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Cont contabil (taguri)" placeholder="Adauga cont..." />
            )}
          />
          <TextField
            label="Denumire furnizor"
            value={form.denumire}
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Numar factura"
            value={form.numar}
            onChange={(e) => setForm((s) => ({ ...s, numar: e.target.value }))}
          />
          <TextField
            type="date"
            label="Data emitere"
            InputLabelProps={{ shrink: true }}
            value={form.data_emitere}
            onChange={(e) => setForm((s) => ({ ...s, data_emitere: e.target.value }))}
          />
          <TextField
            label="Luna emiterii"
            value={lunaEmiterii}
            InputProps={{ readOnly: true }}
          />
          <TextField
            type="date"
            label="Termen scadent"
            InputLabelProps={{ shrink: true }}
            value={form.termen_scadent}
            onChange={(e) => setForm((s) => ({ ...s, termen_scadent: e.target.value }))}
          />
          <TextField
            label="Saptamana scadentei"
            value={saptamanaScadentei}
            InputProps={{ readOnly: true }}
          />
        </Box>

        <Typography className="sf-modal-subtitle">Valute & curs BNR</Typography>
        <Box className="sf-modal-grid">
          <TextField
            select
            label="Valuta"
            value={form.valuta}
            onChange={(e) => setForm((s) => ({ ...s, valuta: e.target.value }))}
          >
            <MenuItem value="USD">USD</MenuItem>
            <MenuItem value="RON">RON</MenuItem>
            <MenuItem value="EUR">EUR</MenuItem>
          </TextField>
          <TextField
            type="number"
            label="Suma EUR/USD"
            value={form.suma_valuta}
            onChange={(e) => setForm((s) => ({ ...s, suma_valuta: e.target.value }))}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {previewLoading ? <CircularProgress size={16} /> : null}
            <Typography variant="body2">
              Suma RON estimata: <strong>{Number(preview.sumaRon || 0).toFixed(2)}</strong>
              {preview.cursBnr ? ` | Curs ${preview.cursBnr} (${preview.cursDate})` : ""}
            </Typography>
          </Box>
          <FormHelperText>
            Pentru EUR/USD se foloseste cursul BNR din ziua emiterii - 1, cu fallback la ultimul curs disponibil.
          </FormHelperText>
        </Box>

        <Typography className="sf-modal-subtitle">Status & documente</Typography>
        <Box className="sf-modal-grid">
          <TextField
            multiline
            minRows={2}
            label="Info"
            value={form.info}
            onChange={(e) => setForm((s) => ({ ...s, info: e.target.value }))}
          />

          <Button component="label" variant="outlined">
            Upload document
            <input hidden type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Button>

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.transmisa_in_spv)}
                onChange={(e) =>
                  setForm((s) => ({ ...s, transmisa_in_spv: e.target.checked }))
                }
              />
            }
            label="Transmisa in SPV"
          />
        </Box>
        {formError ? <FormHelperText error>{formError}</FormHelperText> : null}
          </>
        )}

        {mode === 1 && (
          <Box className="sf-modal-grid">
            <Alert severity="info" className="sf-grid-span-2">
              Incarca PDF-ul facturii. AI extrage automat furnizor, nr factura, moneda, suma, date, apoi creeaza furnizorul (daca lipseste) si factura in DB.
            </Alert>
            <Button component="label" variant="outlined" className="sf-btn-ghost">
              Upload PDF factura
              <input
                hidden
                type="file"
                multiple
                accept="application/pdf"
                onChange={(e) => {
                  const picked = Array.from(e.target.files || []).filter((f) => /\.pdf$/i.test(String(f.name || "")));
                  setAiFiles(picked);
                  setAiError("");
                  setAiResult("");
                }}
              />
            </Button>
            <Typography variant="body2" className="sf-cell-secondary">
              {aiFiles.length ? `${aiFiles.length} fisiere selectate` : "Niciun fisier selectat"}
            </Typography>
            {aiError ? <Alert severity="error" className="sf-grid-span-2">{aiError}</Alert> : null}
            {aiLoading && aiProgress.total > 0 ? (
              <Alert severity="info" className="sf-grid-span-2">
                {`Procesez PDF-uri: ${aiProgress.current} / ${aiProgress.total}`}
              </Alert>
            ) : null}
            {aiResult ? <Alert severity="success" className="sf-grid-span-2">{aiResult}</Alert> : null}
            <Button
              variant="contained"
              className="sf-btn-primary sf-grid-span-2"
              disabled={!aiFiles.length || aiLoading}
              onClick={async () => {
                if (!aiFiles.length) return;
                try {
                  setAiLoading(true);
                  setAiError("");
                  setAiResult("");
                  if (!onAiAutoImport) throw new Error("AI Auto import callback missing");

                  let inserted = 0;
                  let updated = 0;
                  let skipped = 0;
                  let failed = 0;

                  const total = aiFiles.length;
                  setAiProgress({ current: 0, total });

                  for (let i = 0; i < total; i += 1) {
                    const file = aiFiles[i];
                    setAiProgress({ current: i + 1, total });
                    try {
                      const payload = await onAiAutoImport({ file, deferReload: i < total - 1 });
                      const action = String(payload?.action || "inserted").toLowerCase();
                      if (action === "updated") updated += 1;
                      else if (action === "skipped") skipped += 1;
                      else inserted += 1;
                    } catch (_) {
                      failed += 1;
                    }
                  }

                  setAiResult(`Procesare finalizata. inserted=${inserted}, updated=${updated}, skipped=${skipped}, failed=${failed}.`);
                } catch (err) {
                  setAiError(err?.response?.data?.error || err.message || "AI auto import failed");
                } finally {
                  setAiLoading(false);
                }
              }}
            >
              {aiLoading ? <CircularProgress size={18} color="inherit" /> : "Proceseaza cu AI si adauga facturile"}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>Anuleaza</Button>
        <Button variant="contained" onClick={handleSave} disabled={mode !== 0}>
          Salveaza
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InvoiceFormModal;
